import React, { useState, useEffect, useMemo } from 'react';
import { Box, Button, Typography, Paper, Dialog, DialogTitle, DialogContent, DialogActions, Snackbar, Alert, Switch, FormControlLabel } from '@mui/material';
import { DataGrid, GridColDef, GridRenderCellParams, GridRowParams } from '@mui/x-data-grid';
import { Edit, Delete, ContentCopy } from '@mui/icons-material';
import { useNavigate } from 'react-router-dom';
import clientService from '../../services/clientService';
import { Client } from '../../types';
import PageContainer from '../../components/PageContainer';

const Clients: React.FC = () => {
  const [clients, setClients] = useState<Client[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [clientToDelete, setClientToDelete] = useState<Client | null>(null);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error'>('success');
  const [showDisabled, setShowDisabled] = useState(false);
  const navigate = useNavigate();

  // active === true means enabled; everything else (false / null / 0) is disabled
  const visibleClients = useMemo(
    () => (showDisabled ? clients : clients.filter((c) => c.active === true)),
    [clients, showDisabled],
  );
  const disabledCount = useMemo(
    () => clients.filter((c) => c.active !== true).length,
    [clients],
  );

  useEffect(() => {
    const fetchClients = async () => {
      try {
        const data = await clientService.getAll();
        setClients(data);
      } catch (error) {
        console.error('Failed to fetch clients', error);
      } finally {
        setLoading(false);
      }
    };
    fetchClients();
  }, []);

  const handleActiveToggle = async (id: number, newActive: boolean) => {
    try {
      const updated = await clientService.update(id, { active: newActive });
      setClients(clients.map(c => c.id === id ? updated : c));
    } catch (error) {
      console.error('Failed to update active', error);
    }
  };

  const processRowUpdate = async (newRow: Client, oldRow: Client) => {
    const updatedFields: Record<string, any> = {};
    Object.keys(newRow).forEach(key => {
      if (newRow[key] !== oldRow[key]) {
        updatedFields[key] = newRow[key];
      }
    });
    if (Object.keys(updatedFields).length > 0) {
      const updated = await clientService.update(newRow.id, updatedFields);
      return updated;
    }
    return newRow;
  };

  const handleDeleteClick = (client: Client) => {
    setClientToDelete(client);
    setDeleteDialogOpen(true);
  };

  const handleDeleteConfirm = async () => {
    if (!clientToDelete) return;
    
    try {
      await clientService.delete(clientToDelete.id);
      setClients(clients.filter(c => c.id !== clientToDelete.id));
      setSnackbarMessage(`Client '${clientToDelete.agency_name}' deleted successfully`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
    } catch (error) {
      console.error('Failed to delete client', error);
      setSnackbarMessage('Failed to delete client');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setDeleteDialogOpen(false);
      setClientToDelete(null);
    }
  };

  const handleDeleteCancel = () => {
    setDeleteDialogOpen(false);
    setClientToDelete(null);
  };

  const handleDuplicate = (client: Client) => {
    // Create a copy of the client without the id and agency_name
    const { id, agency_name, ...clientData } = client;
    navigate('/app/admin/clients/new', { 
      state: { duplicateData: clientData } 
    });
  };

  const columns: GridColDef[] = [
    {
      field: 'active',
      headerName: 'Enabled',
      width: 100,
      editable: false,
      renderCell: (params: GridRenderCellParams) => (
        <Switch
          checked={params.value || false}
          onChange={(e) => handleActiveToggle(params.row.id, e.target.checked)}
          size="small"
        />
      ),
    },
    { field: 'agency_name', headerName: 'Agency Name', flex: 1, editable: true },
    { field: 'url', headerName: 'URL', flex: 1, editable: true },
    { field: 'contact_name', headerName: 'Contact Name', flex: 1, editable: true },
    { field: 'contact_email', headerName: 'Contact Email', flex: 1, editable: true },
    {
      field: 'actions',
      headerName: '',
      width: 200,
      renderCell: (params: GridRenderCellParams) => (
        <Box sx={{ display: 'flex', gap: 1 }}>
          <Button 
            onClick={() => navigate(`/app/admin/clients/${params.row.id}`)}
            size="small"
            title="Edit"
          >
            <Edit />
          </Button>
          <Button 
            onClick={() => handleDuplicate(params.row)}
            size="small"
            title="Duplicate"
          >
            <ContentCopy />
          </Button>
          <Button 
            onClick={() => handleDeleteClick(params.row)}
            color="error"
            size="small"
            title="Delete"
          >
            <Delete />
          </Button>
        </Box>
      ),
    },
  ];

  return (
    <PageContainer>
      <Typography 
        variant="subtitle1" 
        color="text.secondary"
        sx={{ mb: 3 }}
      >
        Clients
      </Typography>
      <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', mb: 2 }}>
        <FormControlLabel
          control={
            <Switch
              checked={showDisabled}
              onChange={(e) => setShowDisabled(e.target.checked)}
              size="small"
            />
          }
          label={`Show disabled clients${disabledCount ? ` (${disabledCount})` : ''}`}
        />
        <Button variant="contained" onClick={() => navigate('/app/admin/clients/new')}>
          Add Client
        </Button>
      </Box>
      <Paper sx={{ height: 600, width: '100%' }}>
        <DataGrid
          rows={visibleClients}
          columns={columns}
          loading={loading}
          processRowUpdate={processRowUpdate}
          editMode="row"
          getRowId={(row) => row.id}
          sx={{ border: 0 }}
        />
      </Paper>

      {/* Delete Confirmation Dialog */}
      <Dialog open={deleteDialogOpen} onClose={handleDeleteCancel}>
        <DialogTitle>Confirm Delete</DialogTitle>
        <DialogContent>
          <Typography>
            Are you sure you want to delete the client "{clientToDelete?.agency_name}"?
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
            This will permanently delete the client and all associated data including emails, segments, contacts, analytics, and other related information. This action cannot be undone.
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleDeleteCancel}>Cancel</Button>
          <Button onClick={handleDeleteConfirm} color="error" variant="contained">
            Delete Client
          </Button>
        </DialogActions>
      </Dialog>

      {/* Success/Error Snackbar */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={() => setSnackbarOpen(false)}
        anchorOrigin={{ vertical: 'top', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbarOpen(false)}
          severity={snackbarSeverity}
          sx={{ width: '100%' }}
        >
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </PageContainer>
  );
};

export default Clients; 